import { auth } from '../../lib/firebase';
import { getConfig } from '../../config/environment';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import type {
  RemiClientMessage,
  RemiServerMessage,
  RemiConnectionStatus,
  RemiToolUseMessage,
  RemiActionCompleteMessage,
  RemiSessionStartMessage,
  RemiJobStatusMessage,
  RemiAsyncJob,
} from './types';

// ─── Tool label mapping ──────────────────────────────────────────────────────

const TOOL_LABELS: Record<string, string> = {
  search_contacts: 'Searching contacts…',
  search_contacts_crm: 'Searching contacts…',
  list_contacts_crm: 'Loading contacts…',
  get_contact_crm: 'Opening contact…',
  top_lead_scores_crm: 'Prioritizing leads…',
  recent_contacts_crm: 'Checking recent contacts…',
  handle_crm_query: 'Checking CRM…',
  handle_intelligence_query: 'Checking context…',
  handle_outreach_query: 'Checking outreach…',
  handle_debug_query: 'Checking diagnostics…',
  list_live_flows: 'Checking live flows…',
  create_contact: 'Creating contact…',
  update_contact: 'Updating contact…',
  delete_contact: 'Deleting contact…',
  get_pipeline_analytics: 'Pulling analytics…',
  create_strategy: 'Creating strategy…',
  update_strategy: 'Updating strategy…',
  simulate_call: 'Simulating call…',
  update_cadence: 'Updating cadence…',
  recall_memories: 'Recalling memories…',
  store_memory: 'Storing memory…',
  record_decision: 'Recording decision…',
  list_decisions: 'Loading decisions…',
  create_commitment: 'Creating commitment…',
  list_commitments: 'Loading commitments…',
  track_referral: 'Tracking referral…',
  log_property_interest: 'Logging property interest…',
  explain_system: 'Explaining system…',
  check_config: 'Checking config…',
};

function getToolLabel(tool: string): string {
  return TOOL_LABELS[tool] || `Running ${tool.replace(/_/g, ' ')}…`;
}

// ─── Session ID helpers ──────────────────────────────────────────────────────

const SESSION_KEY = 'remi-session-id';
const SESSION_UID_KEY = 'remi-session-uid';
const CONVERSATION_KEY = 'remi-conversation-id';
const CONVERSATION_UID_KEY = 'remi-conversation-uid';

function getSessionId(): string {
  const uid = auth.currentUser?.uid ?? 'anon';
  const storedUid = sessionStorage.getItem(SESSION_UID_KEY);
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id || storedUid !== uid || !id.startsWith(`${uid}-`)) {
    id = `${uid}-${Date.now()}`;
    sessionStorage.setItem(SESSION_KEY, id);
    sessionStorage.setItem(SESSION_UID_KEY, uid);
  }
  return id;
}

function getConversationId(): string {
  const uid = auth.currentUser?.uid ?? 'anon';
  localStorage.removeItem(CONVERSATION_KEY);
  localStorage.removeItem(CONVERSATION_UID_KEY);
  const storedUid = sessionStorage.getItem(CONVERSATION_UID_KEY);
  let id = sessionStorage.getItem(CONVERSATION_KEY);
  if (!id || storedUid !== uid || !id.startsWith(`${uid}-conv-`)) {
    id = `${uid}-conv-${Date.now()}`;
    sessionStorage.setItem(CONVERSATION_KEY, id);
    sessionStorage.setItem(CONVERSATION_UID_KEY, uid);
  }
  return id;
}

export function clearSessionId(): void {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_UID_KEY);
  sessionStorage.removeItem(CONVERSATION_KEY);
  sessionStorage.removeItem(CONVERSATION_UID_KEY);
  localStorage.removeItem(CONVERSATION_KEY);
  localStorage.removeItem(CONVERSATION_UID_KEY);
}

// ─── WebSocket Service ───────────────────────────────────────────────────────

class RemiWebSocketService {
  private ws: WebSocket | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private intentionalClose = false;
  private messageQueue: RemiClientMessage[] = [];
  private useRealtimeGateway = false;
  private realtimeTicket: string | null = null;

  // Callbacks — wired by the store
  onStatusChange: ((status: RemiConnectionStatus) => void) | null = null;
  onStreamChunk: ((content: string) => void) | null = null;
  onMessageComplete: ((content: string, uiBlocks?: unknown[]) => void) | null = null;
  onToolUse: ((msg: RemiToolUseMessage & { label: string }) => void) | null = null;
  onActionComplete: ((msg: RemiActionCompleteMessage) => void) | null = null;
  onError: ((content: string) => void) | null = null;
  onSessionStart: ((msg: RemiSessionStartMessage) => void) | null = null;
  onJobStatus: ((msg: RemiJobStatusMessage) => void) | null = null;

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get isConnecting(): boolean {
    return this.ws?.readyState === WebSocket.CONNECTING;
  }

  // ── Public API ───────────────────────────────────────────────────────────

  async connect(): Promise<void> {
    // Already connected or connecting
    if (this.isConnected || this.isConnecting) return;

    this.intentionalClose = false;
    this.onStatusChange?.('connecting');

    try {
      const url = await this.buildWsUrl();
      this.ws = new WebSocket(url);

      this.ws.onopen = () => this.handleOpen();
      this.ws.onmessage = (e) => this.handleMessage(e);
      this.ws.onclose = (e) => this.handleClose(e);
      this.ws.onerror = () => this.handleError();
    } catch (err) {
      console.error('[RemiWS] connect error:', err);
      this.onStatusChange?.('error');
      this.onError?.('Failed to connect to Remi. Please try again.');
    }
  }

  send(msg: RemiClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      // Buffer message to flush when connection opens
      this.messageQueue.push(msg);
    }
  }

  sendChat(content: string): void {
    this.send({ type: 'message', content });
  }

  sendContext(ctx: {
    contact?: Record<string, unknown>;
    strategy?: Record<string, unknown>;
    cadenceState?: Record<string, unknown>;
    workspace?: 'strategy_builder';
    strategyDraft?: Record<string, unknown>;
    baseRevision?: string;
    backgroundJobs?: RemiAsyncJob[];
    backgroundJobFollowup?: boolean;
  }): void {
    this.send({ type: 'context', ...ctx });
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.stopPing();
    this.clearReconnectTimeout();
    this.messageQueue = [];
    if (this.ws) {
      this.ws.onclose = null; // prevent reconnect handler
      this.ws.close();
      this.ws = null;
    }
    this.onStatusChange?.('disconnected');
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private async buildWsUrl(): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const token = await user.getIdToken(/* forceRefresh */ false);
    const sessionId = getSessionId();
    const { cloudRunServices } = getConfig();
    const org = useOrganizationStore.getState().organization;
    const orgId = org?.id ?? '';
    const conversationId = getConversationId();

    if (cloudRunServices.realtimeEnabled) {
      if (!orgId) throw new Error('Organization is required for Remi realtime');
      const remiApiUrl = `https://remi-api-${cloudRunServices.projectNumber}.us-central1.run.app`;
      const response = await fetch(`${remiApiUrl}/api/v1/realtime/tickets`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organization_id: orgId,
          session_id: sessionId,
          conversation_id: conversationId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Realtime ticket request failed: ${response.status}`);
      }

      const ticketResponse = await response.json();
      this.useRealtimeGateway = true;
      this.realtimeTicket = ticketResponse.ticket;
      return ticketResponse.ws_url || `${cloudRunServices.realtimeWsUrl}/ws/${sessionId}`;
    }

    this.useRealtimeGateway = false;
    this.realtimeTicket = null;
    const host = `remi-${cloudRunServices.projectNumber}.us-central1.run.app`;

    return `wss://${host}/ws/${sessionId}?token=${encodeURIComponent(token)}`;
  }

  private handleOpen(): void {
    this.reconnectAttempts = 0;
    this.onStatusChange?.('connected');
    this.startPing();

    const org = useOrganizationStore.getState().organization;
    const orgId = org?.id ?? '';
    const conversationId = getConversationId();

    if (this.useRealtimeGateway) {
      if (!this.realtimeTicket) {
        this.onError?.('Missing Remi realtime ticket.');
        this.ws?.close();
        return;
      }
      this.ws!.send(
        JSON.stringify({
          type: 'authenticate',
          ticket: this.realtimeTicket,
          organizationId: orgId,
          resumeSession: true,
          conversationId,
        })
      );
      this.realtimeTicket = null;
    } else {
      this.ws!.send(
        JSON.stringify({
          type: 'init',
          organizationId: orgId,
          resumeSession: true,
          conversationId,
        })
      );
    }

    // Flush queued messages
    while (this.messageQueue.length > 0) {
      const msg = this.messageQueue.shift()!;
      this.ws!.send(JSON.stringify(msg));
    }
  }

  private handleMessage(event: MessageEvent): void {
    let msg: RemiServerMessage;
    try {
      msg = JSON.parse(event.data);
    } catch {
      console.warn('[RemiWS] non-JSON message:', event.data);
      return;
    }

    switch (msg.type) {
      case 'session_start':
      case 'session_resumed':
        this.onSessionStart?.(msg);
        break;

      case 'stream':
        this.onStreamChunk?.(msg.content);
        break;

      case 'message':
        this.onMessageComplete?.(msg.content, msg.ui_blocks);
        break;

      case 'tool_use':
        this.onToolUse?.({ ...msg, label: getToolLabel(msg.tool) });
        break;

      case 'action_complete':
        this.onActionComplete?.(msg);
        break;

      case 'error':
        this.onError?.(msg.content);
        break;

      case 'job_status':
        this.onJobStatus?.(msg);
        break;

      case 'pong':
        // no-op
        break;
    }
  }

  private handleClose(event: CloseEvent): void {
    this.stopPing();
    this.ws = null;

    if (this.intentionalClose) {
      this.onStatusChange?.('disconnected');
      return;
    }

    console.warn('[RemiWS] connection closed:', event.code, event.reason);

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect();
    } else {
      this.onStatusChange?.('error');
      this.onError?.('Lost connection to Remi. Please refresh or try again.');
    }
  }

  private handleError(): void {
    console.error('[RemiWS] WebSocket error');
    // onclose will fire after onerror, so reconnect is handled there
  }

  private scheduleReconnect(): void {
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    this.onStatusChange?.('connecting');

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30_000);
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

// Module-level singleton
export const remiWs = new RemiWebSocketService();
